import React, { useEffect, useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { IPolicyDefaultsSession, LifeTime } from '@app/models';
import {PasslyCheckbox, PasslyMaskedInput, PasslyTextField} from '@app/components';
import moment from 'moment';
import 'moment-duration-format';
import { digitRegex, calcDays } from '@app/helpers';

interface IProps {
    checkValidation: Function;
    session: IPolicyDefaultsSession;
    patchData: IData;
}

interface ILifeTimes {
    accessToken: LifeTime;
    refreshToken: LifeTime;
}

interface ILifeTimesErrors {
    accessTokenError: string;
    refreshTokenError: string;
}

interface IData {
    SignOutOnClose: boolean;
    RefreshTokenEnabled: boolean;
    AccessTokenLifetime: string;
    RefreshTokenLifetime: string;
}

enum LifeTimesTypes {
    access = 'access',
    refresh = 'refresh'
}
const MIN_ACCESS_TOKEN_TIME = 5;

export const SessionLifetimes = (props: IProps) => {
    const { checkValidation, session, patchData } = props;
    const { translation } = useTypedTranslation();

    const convertToMinutes = (duration: string): LifeTime => {
        return moment.duration(duration).asMinutes();
    };

    const [signoutOnSessionClose, setSignoutOnSessionClose] = useState<boolean>(patchData?.SignOutOnClose ?? session?.SignOutOnClose ?? false);
    const [lifeTimes, setLifetimes] = useState<ILifeTimes>({
        accessToken: convertToMinutes(patchData?.AccessTokenLifetime ?? session?.AccessTokenLifetime ?? 15),
        refreshToken: convertToMinutes(patchData?.RefreshTokenLifetime ?? session?.RefreshTokenLifetime ?? 480)
    });
    const [lifeTimesErrors, setLifeTimesErrors] = useState<ILifeTimesErrors>({
        accessTokenError: '',
        refreshTokenError: ''
    });
    const [lifeTimesInDays, setLifeTimesInDays] = useState<ILifeTimes>({ accessToken: '', refreshToken: '' });

    const isSameRepresentation = (lifetime: LifeTime, representation: LifeTime): boolean => {
        return representation.toString().indexOf(lifetime.toString()) === 0;
    };

    const handleLifetimeChange = (type: LifeTimesTypes, event: React.ChangeEvent<{ value: unknown }>) => {
        const value: number = event.target.value as number;

        switch (type) {
            case LifeTimesTypes.access:
                setLifetimes({ ...lifeTimes, accessToken: value });
                break;

            case LifeTimesTypes.refresh:
                setLifetimes({ ...lifeTimes, refreshToken: value });
                break;

            default:
                break;
        }
    };

    const convertLifetime = (lifetime: LifeTime): string => {
        return moment.duration(parseInt(lifetime.toString()), 'm').format('DDD.HH:mm:ss.SSS', { trim: false });
    };

    useEffect(() => {
        setLifeTimesInDays({ accessToken: calcDays(lifeTimes.accessToken), refreshToken: calcDays(lifeTimes.refreshToken) });
    }, [lifeTimes]);

    useEffect(() => {
        const accessTokenLifetimeError = Number(lifeTimes.accessToken) < MIN_ACCESS_TOKEN_TIME;
        const refreshTokenLifetimeError = Number(lifeTimes.refreshToken) <= Number(lifeTimes.accessToken);
        const refreshTokenLifetimeNotInRangeError = Number(lifeTimes.refreshToken) > 480;

        if (accessTokenLifetimeError || refreshTokenLifetimeError || refreshTokenLifetimeNotInRangeError) {
            setLifeTimesErrors({
                accessTokenError: accessTokenLifetimeError ? translation('ACCESS_TOKEN_LIFETIME_ERROR') : '',
                refreshTokenError: refreshTokenLifetimeError ? translation('REFRESH_TOKEN_LIFETIME_ERROR') : refreshTokenLifetimeNotInRangeError ? translation('REFRESH_TOKEN_LIFETIME_NOT_IN_RANGE_ERROR') : ''
            });
            checkValidation(false, null);
        } else {
            const data: IData = {
                SignOutOnClose: signoutOnSessionClose,
                RefreshTokenEnabled: true,
                AccessTokenLifetime: convertLifetime(lifeTimes.accessToken),
                RefreshTokenLifetime: convertLifetime(lifeTimes.refreshToken)
            };

            setLifeTimesErrors({ accessTokenError: '', refreshTokenError: '' });
            convertLifetime(lifeTimes.accessToken);
            convertLifetime(lifeTimes.refreshToken);

            checkValidation(true, data);
        }
    }, [signoutOnSessionClose, lifeTimes]);

    const changeSignoutOnSessionClose = () => setSignoutOnSessionClose(!signoutOnSessionClose);

    return (
        <div className='sessionLifetimes'>
            <PasslyCheckbox
                name='signoutOnSessionClose'
                checked={signoutOnSessionClose}
                onChange={changeSignoutOnSessionClose}
                label={translation('POLICY_SIGNOUT_ON_SESSION_CLOSE')}
            />

            <div className='sessionLifetimes__input-block'>
                <div className='sessionLifetimes__input-value'>
                    <div className='form__row'>
                        <PasslyTextField
                            fullWidth
                            key={LifeTimesTypes.access}
                            label='POLICY_ACCESS_TOKEN_LIFETIME'
                            value={lifeTimes.accessToken}
                            onChange={(event: any) => handleLifetimeChange(LifeTimesTypes.access, event)}
                            id='access-token'
                            error={!!lifeTimesErrors.accessTokenError}
                            helperText={lifeTimesErrors.accessTokenError}
                            InputProps={{
                                inputComponent: PasslyMaskedInput,
                                inputProps: {
                                    maskSize: 4,
                                    maskRegex: digitRegex
                                },
                            }} />
                    </div>
                </div>
                <span className='sessionLifetimes__description'>
                    {translation('ACCESS_TOKEN_LIFETIME_UNIT')} {!isSameRepresentation(lifeTimes.accessToken, lifeTimesInDays.accessToken) && `(${lifeTimesInDays.accessToken})`}
                </span>
            </div>

            <div className='sessionLifetimes__input-block'>
                <div className='sessionLifetimes__input-value'>
                    <div className='form__row'>
                        <PasslyTextField
                            fullWidth
                            key={LifeTimesTypes.refresh}
                            label='POLICY_REFRESH_TOKEN_LIFETIME'
                            value={lifeTimes.refreshToken}
                            onChange={(event: any) => handleLifetimeChange(LifeTimesTypes.refresh, event)}
                            id='refresh-token'
                            error={!!lifeTimesErrors.refreshTokenError}
                            helperText={lifeTimesErrors.refreshTokenError}
                            InputProps={{
                                inputComponent: PasslyMaskedInput,
                                inputProps: {
                                    maskSize: 6,
                                    maskRegex: digitRegex
                                },
                            }} />
                    </div>
                </div>
                <span className='sessionLifetimes__description'>
                    {translation('REFRESH_TOKEN_LIFETIME_UNIT')} {!isSameRepresentation(lifeTimes.refreshToken, lifeTimesInDays.refreshToken) && `(${lifeTimesInDays.refreshToken})`}
                </span>
            </div>

        </div>
    );
};
